import type { AuthorizationClaimsV2, TokenPayload } from './tokenTypes';

const CLAIM_KEYS = ['claims', 'permissions', 'scope', 'cognito:groups'] as const;

export const extractClaimsFromPayload = (payload: TokenPayload): string[] =>
    isAuthorizationClaimsV2(payload.claims)
        ? extractClaimsFromV2Payload(payload.claims)
        : extractClaimsFromLegacyPayload(payload);

function extractClaimsFromV2Payload(claims: AuthorizationClaimsV2) {
    if (!Array.isArray(claims.ids_list) || !Array.isArray(claims.grants)) {
        return [];
    }

    const extractedClaims = new Set<string>();

    for (const grant of claims.grants) {
        if (
            !grant ||
            typeof grant.r !== 'string' ||
            !grant.r.trim() ||
            !Array.isArray(grant.a) ||
            !Array.isArray(grant.i)
        ) {
            continue;
        }

        for (const action of grant.a) {
            if (typeof action !== 'string' || !action.trim()) {
                continue;
            }

            for (const idIndex of grant.i) {
                if (!Number.isInteger(idIndex) || idIndex < 0 || idIndex >= claims.ids_list.length) {
                    continue;
                }

                const id = claims.ids_list[idIndex];
                if (typeof id === 'string' && id) {
                    extractedClaims.add(`${grant.r}:${action}:${id}`);
                }
            }
        }
    }
    return [...extractedClaims];
}

function extractClaimsFromLegacyPayload(payload: TokenPayload) {
    const extractedClaims = new Set<string>();

    for (const key of CLAIM_KEYS) {
        const payloadValues = payload[key];

        if (Array.isArray(payloadValues)) {
            payloadValues.forEach(payloadValue => payloadValue.trim() && extractedClaims.add(payloadValue.trim()));
            continue;
        }

        if (typeof payloadValues === 'string') {
            payloadValues.split(/\s+/).forEach(payloadValue => payloadValue && extractedClaims.add(payloadValue));
            continue;
        }

        if (payloadValues && typeof payloadValues === 'object') {
            for (const [k, val] of Object.entries(payloadValues)) {
                const truthy =
                    val === true ||
                    (typeof val === 'number' && val !== 0) ||
                    (typeof val === 'string' && val.length > 0);
                if (truthy) {
                    extractedClaims.add(k);
                }
            }
        }
    }

    return Array.from(extractedClaims);
}

const isAuthorizationClaimsV2 = (value: unknown): value is AuthorizationClaimsV2 =>
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    (value as { version?: unknown }).version === 2;
